import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { toast } from 'sonner';
import { getAccounts } from '../../service/api/Order';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import { createFacturation } from '../../service/api/facturationService';
import { getTotalCashSecurity } from '../../service/api/SecurityService';

const CreateFactureModal = ({ onSuccess }) => {
  const { t } = useTranslation('facturation');
  const selectedDomain = useSelectedDomain();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [aucData, setAucData] = useState({});
  const [checkingAuc, setCheckingAuc] = useState(false);
  
  const [formData, setFormData] = useState({
    Mode: '1',
    AccountId: '',
    SelectedAccountIds: [],
    NumberOfAccounts: '',
    Commissions: '',
    FacturationDate: new Date().toISOString().split('T')[0],
    DomainId: '1'
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [aucErrors, setAucErrors] = useState({});

  const modeOptions = [
    { value: '1', label: t('modals.create.mode.options.single') },
    { value: '2', label: t('modals.create.mode.options.all') },
    { value: '3', label: t('modals.create.mode.options.selected') }
  ];

  const loadAccounts = async () => {
    if (!selectedDomain?.id) return;
    
    try {
      const accountsData = await getAccounts(selectedDomain.id);
      const accountsArray = accountsData.items || [];
      
      const formattedAccounts = accountsArray.map(account => ({
        value: account.id.toString(),
        label: account.companyName || `${account.firstName} ${account.lastName}`.trim() || account.email,
        ...account
      }));
      
      setAccounts(formattedAccounts);
    } catch (error) {
      toast.error(t('errors.loadAccounts'));
    }
  };

  useEffect(() => {
    if (selectedDomain?.id) {
      loadAccounts();
      setFormData(prev => ({ ...prev, DomainId: selectedDomain.id.toString() }));
    }
  }, [selectedDomain]);

  const checkAccountAuc = async (accountId) => {
    if (!accountId || !selectedDomain?.id) return null;
    
    try {
      setCheckingAuc(true);
      const totalAuc = await getTotalCashSecurity(accountId, selectedDomain.id);
      return totalAuc;
    } catch (error) {
      console.error(`Error checking AUC for account ${accountId}:`, error);
      return null;
    } finally {
      setCheckingAuc(false);
    }
  };

  useEffect(() => {
    const verifySelectedAccountAuc = async () => {
      if (formData.Mode === '1' && formData.AccountId && selectedDomain?.id) {
        const auc = await checkAccountAuc(formData.AccountId);
        setAucData(prev => ({
          ...prev,
          [formData.AccountId]: auc
        }));
        
        if (auc !== null && auc <= 0) {
          setAucErrors(prev => ({
            ...prev,
            AccountId: t('modals.create.errors.insufficientAuc', { value: auc })
          }));
        } else {
          setAucErrors(prev => ({
            ...prev,
            AccountId: ''
          }));
        }
      }
    };
    
    verifySelectedAccountAuc();
  }, [formData.AccountId, formData.Mode, selectedDomain]);

  useEffect(() => {
    const verifySelectedAccountsAuc = async () => {
      if (formData.Mode === '3' && formData.SelectedAccountIds.length > 0 && selectedDomain?.id) {
        const newAucErrors = {};
        const newAucData = { ...aucData };
        
        for (const accountId of formData.SelectedAccountIds) {
          const auc = await checkAccountAuc(accountId);
          newAucData[accountId] = auc;
          
          if (auc !== null && auc <= 0) {
            const account = accounts.find(acc => acc.value === accountId);
            const accountName = account?.label || t('modals.create.auc.account', { id: accountId });
            newAucErrors[accountId] = `${accountName}: ${t('modals.create.auc.insufficient', { value: auc })}`;
          }
        }
        
        setAucData(newAucData);
        setAucErrors(newAucErrors);
      }
    };
    
    if (formData.SelectedAccountIds.length > 0) {
      verifySelectedAccountsAuc();
    }
  }, [formData.SelectedAccountIds, formData.Mode, selectedDomain]);

  const validateForm = () => {
    const newErrors = {};

    // Validation commission
    if (!formData.Commissions || formData.Commissions.trim() === '') {
      newErrors.Commissions = t('modals.create.errors.required');
    } else {
      const commission = parseFloat(formData.Commissions.replace(',', '.'));
      if (isNaN(commission) || commission <= 0 || commission > 100) {
        newErrors.Commissions = t('modals.create.errors.invalidCommission');
      }
    }

    // Validation date
    if (!formData.FacturationDate) {
      newErrors.FacturationDate = t('modals.create.errors.required');
    } else {
      const selectedDate = new Date(formData.FacturationDate);
      const today = new Date();
      if (selectedDate > today) {
        newErrors.FacturationDate = t('modals.create.errors.futureDate');
      }
    }

    // Validation selon le mode
    switch (formData.Mode) {
      case '1':
        if (!formData.AccountId) {
          newErrors.AccountId = t('modals.create.errors.invalidAccount');
        }
        break;
      case '2':
        if (formData.NumberOfAccounts && (isNaN(formData.NumberOfAccounts) || formData.NumberOfAccounts <= 0)) {
          newErrors.NumberOfAccounts = t('modals.create.errors.invalidCount');
        }
        break;
      case '3':
        if (!formData.NumberOfAccounts || formData.NumberOfAccounts <= 0) {
          newErrors.NumberOfAccounts = t('modals.create.errors.required');
        }
        if (!formData.SelectedAccountIds || formData.SelectedAccountIds.length === 0) {
          newErrors.SelectedAccountIds = t('modals.create.errors.noAccounts');
        } else if (formData.NumberOfAccounts && formData.SelectedAccountIds.length > formData.NumberOfAccounts) {
          newErrors.SelectedAccountIds = t('modals.create.errors.maxAccounts', {
            current: formData.SelectedAccountIds.length,
            limit: formData.NumberOfAccounts
          });
        }
        break;
    }

    // Validation AUC
    if (formData.Mode === '1' && formData.AccountId && aucData[formData.AccountId] !== undefined) {
      if (aucData[formData.AccountId] <= 0) {
        newErrors.AccountId = aucErrors.AccountId || t('modals.create.errors.insufficientAuc', { value: aucData[formData.AccountId] });
      }
    }

    if (formData.Mode === '3' && Object.keys(aucErrors).length > 0) {
      newErrors.SelectedAccountIds = t('modals.create.errors.removeInvalidAuc');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateForm();
  };

  const getFieldClass = (field) => {
    return `form-control ${errors[field] && touched[field] ? 'is-invalid' : ''}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      Commissions: true,
      FacturationDate: true,
      AccountId: true,
      NumberOfAccounts: true,
      SelectedAccountIds: true
    });

    // Vérification finale AUC avant soumission
    if (formData.Mode === '1' && formData.AccountId) {
      const finalAuc = await checkAccountAuc(formData.AccountId);
      if (finalAuc !== null && finalAuc <= 0) {
        toast.error(`❌ ${t('modals.create.auc.insufficient', { value: finalAuc })}`);
        return;
      }
    }

    if (formData.Mode === '3' && formData.SelectedAccountIds.length > 0) {
      let hasInvalidAuc = false;
      for (const accountId of formData.SelectedAccountIds) {
        const finalAuc = await checkAccountAuc(accountId);
        if (finalAuc !== null && finalAuc <= 0) {
          hasInvalidAuc = true;
          const account = accounts.find(acc => acc.value === accountId);
          const accountName = account?.label || t('modals.create.auc.account', { id: accountId });
          toast.error(`❌ ${accountName}: ${t('modals.create.auc.insufficient', { value: finalAuc })}`);
        }
      }
      if (hasInvalidAuc) {
        toast.error(`❌ ${t('modals.create.errors.removeInvalidAuc')}`);
        return;
      }
    }

    if (!validateForm()) {
      toast.error(t('validation.errors.invalidForm'));
      return;
    }

    if (!selectedDomain?.id) {
      toast.error(t('validation.errors.noDomain'));
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      
      submitData.append('Mode', formData.Mode);
      submitData.append('Commissions', formData.Commissions.replace(',', '.'));
      submitData.append('FacturationDate', new Date(formData.FacturationDate).toISOString());
      submitData.append('DomainId', selectedDomain.id.toString());

      if (formData.Mode === '1' && formData.AccountId) {
        submitData.append('AccountId', formData.AccountId);
      }

      if (formData.Mode === '2' && formData.NumberOfAccounts) {
        submitData.append('NumberOfAccounts', formData.NumberOfAccounts);
      }

      if (formData.Mode === '3') {
        if (formData.NumberOfAccounts) {
          submitData.append('NumberOfAccounts', formData.NumberOfAccounts);
        }
        if (formData.SelectedAccountIds && formData.SelectedAccountIds.length > 0) {
          formData.SelectedAccountIds.forEach(id => {
            submitData.append('SelectedAccountIds', id);
          });
        }
      }

      const result = await createFacturation(submitData);
      
      if (result.success) {
        toast.success(`✅ ${result.message || t('success.created')}`);
        resetForm();
        document.getElementById('closeCreateFactureModal').click();
        onSuccess?.();
      } else {
        toast.error(`❌ ${result.message || t('errors.create')}`);
      }
    } catch (error) {
      console.error('Error creating facturation:', error);
      const errorMessage = error.response?.data?.message || t('errors.create');
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      Mode: '1',
      AccountId: '',
      SelectedAccountIds: [],
      NumberOfAccounts: '',
      Commissions: '',
      FacturationDate: new Date().toISOString().split('T')[0],
      DomainId: selectedDomain?.id?.toString() || '1'
    });
    setErrors({});
    setTouched({});
    setAucData({});
    setAucErrors({});
  };

  const handleModeChange = (mode) => {
    setFormData({
      Mode: mode,
      AccountId: '',
      SelectedAccountIds: [],
      NumberOfAccounts: '',
      Commissions: formData.Commissions,
      FacturationDate: formData.FacturationDate,
      DomainId: formData.DomainId
    });
    setErrors(prev => ({
      ...prev,
      AccountId: '',
      NumberOfAccounts: '',
      SelectedAccountIds: ''
    }));
  };

  const getCommissionColor = (commission) => {
    const comm = parseFloat(commission?.replace(',', '.')) || 0;
    if (comm < 2) return 'text-success';
    if (comm < 5) return 'text-warning';
    return 'text-danger';
  };

  const getSummary = () => {
    let compteText = '';
    switch (formData.Mode) {
      case '1':
        const selectedAccount = accounts.find(acc => acc.value === formData.AccountId);
        compteText = selectedAccount ? `1 compte (${selectedAccount.label})` : t('modals.create.summary.noAccount');
        break;
      case '2':
        compteText = formData.NumberOfAccounts 
          ? t('modals.create.summary.maxAccounts', { count: formData.NumberOfAccounts }) 
          : t('modals.create.summary.allAccounts');
        break;
      case '3':
        compteText = formData.SelectedAccountIds.length > 0 
          ? t('modals.create.summary.selectedAccounts', { count: formData.SelectedAccountIds.length }) 
          : t('modals.create.summary.noAccount');
        break;
    }

    return {
      mode: modeOptions.find(m => m.value === formData.Mode)?.label,
      comptes: compteText,
      commission: formData.Commissions ? `${formData.Commissions}%` : t('modals.create.summary.noCommission'),
      date: formData.FacturationDate ? new Date(formData.FacturationDate).toLocaleDateString('fr-FR') : t('modals.create.summary.noDate')
    };
  };

  const getAucIndicator = (accountId) => {
    const auc = aucData[accountId];
    if (auc === undefined) return null;
    
    if (auc > 0) {
      return (
        <span className="text-success ms-2">
          ✓ {t('modals.create.auc.available', { value: auc })}
        </span>
      );
    } else if (auc <= 0) {
      return (
        <span className="text-danger ms-2">
          ✗ {t('modals.create.auc.insufficient', { value: auc })}
        </span>
      );
    }
    return null;
  };

  const formatOptionLabel = (option, { context }) => {
    const aucIndicator = getAucIndicator(option.value);
    
    return (
      <div className="d-flex justify-content-between align-items-center">
        <span>{option.label}</span>
        {aucIndicator}
      </div>
    );
  };

  const summary = getSummary();

  return (
    <>
      <button 
        className="btn btn-primary d-none" 
        data-bs-toggle="modal" 
        data-bs-target="#createFactureModal"
        id="openCreateFactureModal"
      >
        {t('modals.create.actions.open')}
      </button>

      <div className="modal fade" id="createFactureModal" tabIndex="-1" aria-labelledby="createFactureModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-white text-white">
              <h5 className="modal-title" id="createFactureModalLabel">
                {t('modals.create.title')}
              </h5>
              <button type="button" className="btn-close btn-close-primary" data-bs-dismiss="modal" aria-label="Close" id="closeCreateFactureModal"></button>
            </div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-body">
                {/* Mode de facturation */}
                <div className="row mb-4">
                  <div className="col-12">
                    <label className="form-label fw-semibold">{t('modals.create.mode.label')}</label>
                    <div className="d-grid gap-2">
                      {modeOptions.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          className={`btn ${formData.Mode === option.value ? 'btn-primary' : 'btn-outline-primary'} text-start`}
                          onClick={() => handleModeChange(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Champs conditionnels selon le mode */}
                <div className="row mb-3">
                  {formData.Mode === '1' && (
                    <div className="col-12">
                      <label className="form-label fw-semibold">
                        {t('modals.create.account.label')}
                        {checkingAuc && formData.AccountId && (
                          <span className="ms-2 spinner-border spinner-border-sm text-primary" role="status"></span>
                        )}
                      </label>
                      <Select
                        options={accounts}
                        value={accounts.find(acc => acc.value === formData.AccountId)}
                        onChange={(selected) => handleChange('AccountId', selected?.value || '')}
                        onBlur={() => handleBlur('AccountId')}
                        placeholder={t('modals.create.account.placeholder')}
                        isClearable
                        isLoading={accounts.length === 0}
                        className={errors.AccountId && touched.AccountId ? 'is-invalid' : ''}
                        formatOptionLabel={formatOptionLabel}
                      />
                      {errors.AccountId && touched.AccountId && (
                        <div className="invalid-feedback d-block">
                          {errors.AccountId}
                        </div>
                      )}
                      {formData.AccountId && aucData[formData.AccountId] !== undefined && !errors.AccountId && (
                        <div className={`small mt-1 ${aucData[formData.AccountId] > 0 ? 'text-success' : 'text-danger'}`}>
                          {aucData[formData.AccountId] > 0 
                            ? t('modals.create.auc.available', { value: aucData[formData.AccountId] })
                            : t('modals.create.auc.insufficient', { value: aucData[formData.AccountId] })
                          }
                        </div>
                      )}
                    </div>
                  )}

                  {formData.Mode === '2' && (
                    <div className="col-12">
                      <label className="form-label fw-semibold">{t('modals.create.count.label')}</label>
                      <input
                        type="number"
                        className={getFieldClass('NumberOfAccounts')}
                        placeholder={t('modals.create.count.placeholder')}
                        value={formData.NumberOfAccounts}
                        onChange={(e) => handleChange('NumberOfAccounts', e.target.value)}
                        onBlur={() => handleBlur('NumberOfAccounts')}
                        min="1"
                      />
                      {errors.NumberOfAccounts && touched.NumberOfAccounts && (
                        <div className="invalid-feedback">
                          {errors.NumberOfAccounts}
                        </div>
                      )}
                      <div className="form-text">
                        💡 {t('modals.create.count.tooltip')}
                      </div>
                    </div>
                  )}

                  {formData.Mode === '3' && (
                    <>
                      <div className="col-12 mb-3">
                        <label className="form-label fw-semibold">{t('modals.create.selectedCount.label')}</label>
                        <input
                          type="number"
                          className={getFieldClass('NumberOfAccounts')}
                          placeholder={t('modals.create.selectedCount.placeholder')}
                          value={formData.NumberOfAccounts}
                          onChange={(e) => handleChange('NumberOfAccounts', e.target.value)}
                          onBlur={() => handleBlur('NumberOfAccounts')}
                          min="1"
                        />
                        {errors.NumberOfAccounts && touched.NumberOfAccounts && (
                          <div className="invalid-feedback">
                            {errors.NumberOfAccounts}
                          </div>
                        )}
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">{t('modals.create.account.multiple')}</label>
                        <Select
                          isMulti
                          options={accounts}
                          value={accounts.filter(acc => formData.SelectedAccountIds.includes(acc.value))}
                          onChange={(selected) => handleChange('SelectedAccountIds', selected.map(s => s.value))}
                          onBlur={() => handleBlur('SelectedAccountIds')}
                          placeholder={t('modals.create.account.placeholder')}
                          isClearable
                          isLoading={accounts.length === 0}
                          className={errors.SelectedAccountIds && touched.SelectedAccountIds ? 'is-invalid' : ''}
                        />
                        {formData.Mode === '3' && formData.SelectedAccountIds.length > 0 && Object.keys(aucErrors).length > 0 && (
                          <div className="col-12 mt-2">
                            <div className="alert alert-warning py-2">
                              <small>
                                <strong>⚠️ {t('modals.create.errors.removeInvalidAuc')}:</strong>
                                <ul className="mb-0 mt-1">
                                  {Object.entries(aucErrors).map(([accountId, error]) => (
                                    <li key={accountId}>
                                      {error}
                                    </li>
                                  ))}
                                </ul>
                              </small>
                            </div>
                          </div>
                        )}
                        {errors.SelectedAccountIds && touched.SelectedAccountIds && (
                          <div className="invalid-feedback d-block">
                            {errors.SelectedAccountIds}
                          </div>
                        )}
                        {formData.NumberOfAccounts && formData.SelectedAccountIds.length > 0 && (
                          <div className="form-text">
                            {t('modals.create.selectedCount.selected', {
                              current: formData.SelectedAccountIds.length,
                              total: formData.NumberOfAccounts
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Informations de facturation */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">{t('modals.create.commission.label')}</label>
                    <input
                      type="text"
                      className={getFieldClass('Commissions')}
                      value={formData.Commissions}
                      onChange={(e) => handleChange('Commissions', e.target.value)}
                      onBlur={() => handleBlur('Commissions')}
                      placeholder={t('modals.create.commission.placeholder')}
                      required
                    />
                    {errors.Commissions && touched.Commissions && (
                      <div className="invalid-feedback">
                        {errors.Commissions}
                      </div>
                    )}
                    {formData.Commissions && !errors.Commissions && (
                      <div className={`small fw-semibold ${getCommissionColor(formData.Commissions)}`}>
                        {t('modals.create.commission.rate', { value: formData.Commissions })}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">{t('modals.create.date.label')}</label>
                    <input
                      type="date"
                      className={getFieldClass('FacturationDate')}
                      value={formData.FacturationDate}
                      onChange={(e) => handleChange('FacturationDate', e.target.value)}
                      onBlur={() => handleBlur('FacturationDate')}
                      required
                    />
                    {errors.FacturationDate && touched.FacturationDate && (
                      <div className="invalid-feedback">
                        {errors.FacturationDate}
                      </div>
                    )}
                  </div>
                </div>

                {/* Résumé */}
                <div className="alert alert-info mt-3">
                  <h6 className="alert-heading">{t('modals.create.summary.title')}</h6>
                  <div className="small">
                    <strong>{t('modals.create.summary.mode')}:</strong> {summary.mode}<br/>
                    <strong>{t('modals.create.summary.accounts')}:</strong> {summary.comptes}<br/>
                    <strong>{t('modals.create.summary.commission')}:</strong> {summary.commission}<br/>
                    <strong>{t('modals.create.summary.date')}:</strong> {summary.date}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  {t('modals.create.actions.cancel')}
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {t('modals.create.actions.creating')}
                    </>
                  ) : (
                    t('modals.create.actions.create')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateFactureModal;